import React, { useCallback, useEffect, useMemo, useState } from "react";
import AddMaterialInScope from "./AddMaterialInScope";
import ReactQuill from "react-quill";
import SearchableDropdown from "./SearchableDropdown";
import { ErrorMessage, useFormikContext } from "formik";
import StaticPopupNew from "components/StaticPopupNew";
import { debounce } from "lodash";

const MemoizedQuill = React.memo(ReactQuill);

const SectionPopup = ({
  section,
  sectionIndex,
  handleClosePopup,
  setFieldValue,
  values,
  unitOptions,
}) => {
  const { validateForm, setTouched } = useFormikContext();

  const [localSection, setLocalSection] = useState(() =>
    section
      ? {
          ...section,
          scopes: (section.scopes || []).map((s) => ({
            ...s,
            preferredBrandsInput:
              Array.isArray(s.preferredBrands) && s.preferredBrands.length
                ? s.preferredBrands.join(", ")
                : "",
          })),
        }
      : section
  );

  useEffect(() => {
    if (!section) return;
    setLocalSection({
      ...section,
      scopes: (section.scopes || []).map((s) => ({
        ...s,
        preferredBrandsInput:
          Array.isArray(s.preferredBrands) && s.preferredBrands.length
            ? s.preferredBrands.join(", ")
            : "",
      })),
    });
  }, [section]);

  const cleanSectionData = useCallback((sectionData) => {
    const cleanedScopes = (sectionData.scopes || []).map((scope) => {
      const cleanedMaterials = (scope.materials || []).filter((m) => {
        return (
          m.name?.trim() ||
          m.quantity ||
          m.unit?.trim() ||
          (m.brands && m.brands.length > 0) ||
          m.specifications?.trim()
        );
      });
      const { preferredBrandsInput, ...rest } = scope;
      return { ...rest, materials: cleanedMaterials };
    });
    return { ...sectionData, scopes: cleanedScopes };
  }, []);

  const debouncedSync = useMemo(
    () =>
      debounce(async (updatedSection) => {
        const cleaned = cleanSectionData(updatedSection);

        setFieldValue(
          "providingFixingDetails",
          values.providingFixingDetails.map((sec, i) =>
            i === sectionIndex ? cleaned : sec
          )
        );

        const updatedTouched = { ...(values.touched || {}) };
        updatedTouched.providingFixingDetails =
          updatedTouched.providingFixingDetails || [];
        updatedTouched.providingFixingDetails[sectionIndex] = {
          scopes: cleaned.scopes.map((scope) => ({
            scopeOfWork: true,
            quantity: true,
            unit: true,
            materials: (scope.materials || []).map(() => ({
              name: true,
              quantity: true,
              unit: true,
            })),
          })),
        };
        setTouched(updatedTouched, false);

        await validateForm();
      }, 400),
    [values, sectionIndex, setFieldValue, setTouched, validateForm, cleanSectionData]
  );

  const updateSection = useCallback(
    (updater) => {
      setLocalSection((prev) => {
        const updated =
          typeof updater === "function" ? updater(prev) : updater;
        debouncedSync(updated);
        return updated;
      });
    },
    [debouncedSync]
  );

  const handleScopeChange = useCallback(
    (scopeIndex, field, value) => {
      updateSection((prev) => ({
        ...prev,
        scopes: prev.scopes.map((scope, i) =>
          i === scopeIndex ? { ...scope, [field]: value } : scope
        ),
      }));
    },
    [updateSection]
  );

  const handlePreferredBrandsInputChange = useCallback(
    (scopeIndex, inputValue) => {
      // Preserve raw input text
      const brandsArray = inputValue
        .split(",")
        .map((b) => b.trim());

      updateSection((prev) => ({
        ...prev,
        scopes: prev.scopes.map((scope, i) =>
          i === scopeIndex
            ? {
                ...scope,
                preferredBrandsInput: inputValue, // keep comma visible
                preferredBrands: brandsArray, // still sync to array
              }
            : scope
        ),
      }));
    },
    [updateSection]
  );

  const handleAddScope = useCallback(() => {
    updateSection((prev) => ({
      ...prev,
      scopes: [
        ...prev.scopes,
        {
          scopeOfWork: "",
          quantity: "",
          unit: "",
          budget: "",
          preferredBrands: [],
          preferredBrandsInput: "",
          materials: [],
          isMaterialMode: false,
        },
      ],
    }));
  }, [updateSection]);

  const handleRemoveScope = useCallback(
    (scopeIndex) => {
      updateSection((prev) => ({
        ...prev,
        scopes: prev.scopes.filter((_, i) => i !== scopeIndex),
      }));
    },
    [updateSection]
  );

  const handleMaterialsUpdate = useCallback(
    (scopeIndex, updatedMaterials) => {
      updateSection((prev) => ({
        ...prev,
        scopes: prev.scopes.map((scope, i) =>
          i === scopeIndex ? { ...scope, materials: updatedMaterials,   isMaterialMode: updatedMaterials.length > 0, } : scope
        ),
      }));
    },
    [updateSection]
  );

  useEffect(() => () => debouncedSync.cancel(), [debouncedSync]);

  if (!localSection) return null;

  return (
    <StaticPopupNew
      openModal={true}
      handleClose={() => handleClosePopup(sectionIndex)}
      width={"6xl"}
      height={50}
      bodyStyle="pb-0 pt-0 px-0"
      className="px-0 text-sm"
      header={localSection.sectionName}
      dismissible={false}
      showCloseButton={false}
    >
      <div className="mb-4 px-4">
        {/* Header */}
        <div className="grid grid-cols-22 gap-1 border-b border-gray-300 sticky top-0 z-10 pr-2 pl-4 py-2 bg-gray-100">
          <p className="text-sm col-span-1">Sr#</p>
          <p className="text-sm col-span-8">
            Description (Work Details)
            <span className="text-red-500 ml-0.5">*</span>
          </p>
          <p className="text-sm col-span-2">
            Qty<span className="text-red-500 ml-0.5">*</span>
          </p>
          <p className="text-sm col-span-3">
            Unit<span className="text-red-500 ml-0.5">*</span>
          </p>
          <p className="text-sm col-span-2">
            Budget
          </p>
          <div className="text-sm col-span-6 flex justify-between">
            <p>Preferred Brands (comma separated)</p>
          </div>
        </div>

        {/* Scopes */}
        {localSection.scopes.map((scope, scopeIndex) => {
        const hasMaterials = !!scope.isMaterialMode;

    return(<div key={scopeIndex} className="pt-2 border-b border-gray-600">
            <div className="grid grid-cols-22 gap-1 pb-2">
              <p className="text-xs col-span-1 text-center">
                SoW {scopeIndex + 1}
              </p>

              {/* Work Details */}
              <div className="col-span-8">
                <MemoizedQuill
                  value={scope.scopeOfWork}
                  onChange={(value) =>
                    handleScopeChange(scopeIndex, "scopeOfWork", value)
                  }
                  theme="snow"
                  modules={{ toolbar: false }}
                  className="h-full max-h-[150px] overflow-y-auto remove-quill-padding"
                />
                <ErrorMessage
                  name={`providingFixingDetails[${sectionIndex}].scopes[${scopeIndex}].scopeOfWork`}
                  component="div"
                  className="text-red-500 text-xs pt-1 "
                />
              </div>

              {/* Quantity */}
              <div className="col-span-2 ">
                {!hasMaterials && (<div className="self-end relative">
                <input
                  type="text"
                  value={scope.quantity || ""}
                  onChange={(e) =>{
                    const inputValue = e.target.value;
                          if (/^\d{0,10}(\.\d{0,2})?$/.test(inputValue) || inputValue === "") {
                            handleScopeChange(scopeIndex, "quantity", inputValue);
                          }
                        }}
                  className="border focus:ring-0 border-gray-300 p-1 h-[28px] w-full text-xs"
                />
                <ErrorMessage
                  name={`providingFixingDetails[${sectionIndex}].scopes[${scopeIndex}].quantity`}
                  component="div"
                  className="absolute text-red-500 text-xs mt-1"
                />
              </div>)}
              </div>

              {/* Unit */}
             <div className="col-span-3 ">
              {!hasMaterials && (<div className="relative self-end">
                <SearchableDropdown
                  value={scope.unit}
                  options={unitOptions}
                  onChange={(value) =>
                    handleScopeChange(scopeIndex, "unit", value)
                  }
                />
                <ErrorMessage
                  name={`providingFixingDetails[${sectionIndex}].scopes[${scopeIndex}].unit`}
                  component="div"
                  className="absolute text-red-500 text-xs mt-1"
                />
              </div>)}
              </div>

              {/* budget */}
             
              <div className="col-span-2 ">
                 {!hasMaterials && (<div className="self-end relative">
                <input
                  type="text"
                  value={scope?.budget || ""}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (/^\d{0,10}(\.\d{0,3})?$/.test(inputValue) || inputValue === "") {
                      handleScopeChange(scopeIndex, "budget", inputValue);
                    }
                  }}
                  className="border focus:ring-0 border-gray-300 p-1 h-[28px] w-full text-xs"
                />
                <ErrorMessage
                  name={`providingFixingDetails[${sectionIndex}].scopes[${scopeIndex}].budget`}
                  component="div"
                  className="absolute text-red-500 text-xs mt-1"
                />
              </div>
            )}
            </div>

              <div className="col-span-5 ">
                {!hasMaterials && (<div className="self-end">
                <input
                  type="text"
                  value={
                    scope.preferredBrandsInput !== undefined
                      ? scope.preferredBrandsInput
                      : Array.isArray(scope.preferredBrands)
                      ? scope.preferredBrands.join(", ")
                      : ""
                  }
                  onChange={(e) =>
                    handlePreferredBrandsInputChange(scopeIndex, e.target.value)
                  }
                  placeholder="Enter brands (comma separated)"
                  className="border border-gray-300 p-1 h-[28px] w-full text-xs focus:outline-none focus:ring-0"
                />
                <ErrorMessage
                  name={`providingFixingDetails[${sectionIndex}].scopes[${scopeIndex}].preferredBrands`}
                  component="div"
                  className="text-red-500 text-xs"
                />
              </div>)}
              </div>

              {/* Add / Remove Scope Buttons */}
              <div className="flex justify-evenly self-end">
                <button type="button" onClick={handleAddScope}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 5v14m7-7H5"
                    />
                  </svg>
                </button>
                {localSection.scopes.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveScope(scopeIndex)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 12h14"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Materials */}
            <AddMaterialInScope
              materials={scope.materials}
              onAddMaterial={(updatedMaterials) =>
                handleMaterialsUpdate(scopeIndex, updatedMaterials)
              }
              sectionIndex={sectionIndex}
              scopeIndex={scopeIndex}
            />
          </div>)
        })}
      </div>

      {/* Footer */}
      <div
        style={{ boxShadow: "0px -2px 4px -2px rgba(0, 0, 0, 0.2)" }}
        className="w-full sticky bottom-0 bg-white rounded-b-lg border-t justify-between items-center text-sm flex px-4 py-2"
      >
        <span className="italic text-xs">*SoW - Scope of Work</span>
        <div className="space-x-2">
          <button
            onClick={() => handleClosePopup(sectionIndex)}
            type="button"
            className="px-4 py-1 border hover:border-black"
          >
            Close
          </button>
        </div>
      </div>
    </StaticPopupNew>
  );
};

export default SectionPopup;
